// 戴冠獣の16コマの歩き (2026-11-04・開発版だけ ?houndWalk=16)。配布版は8コマのまま (src/hound_walk8_1104.ts)。
//   作者の答え: カクつきの直し方は frames16 (db answers/hound-walk-judder-1104)。
//   絵 = 8コマの間に1枚ずつ、前のコマを Codex が編集して脚だけ動かした8枚 (模型 hound-walk-real-slow の真ん中の時刻の下書きどおり)。
//     4枚目は1枚だけ明るく灰色がかっていたので、色を両どなり (t250・t500) にそろえた houndWalk16_t375 を使う。
//   記録 = docs/research/2026-11-04-hound-walk-16frames.md
//   1周の距離は8コマと同じ。大きさ・横の置き場所は絵から測った値 (sprites.ts の POSE_SCALE_FIX・HOUND_PASS_ANCHOR_X)。
import { HOUND_PASS_ANCHOR_X, setWalkCycleFracHook, setWalkCycleOverride, type WalkCycleSmooth } from './sprites'
import { HOUND_WALK8_STRIDE_PX, setHoundWalk8 } from './hound_walk8_1104'
import type { SpriteKey } from './types'

export const HOUND_WALK16_KEYS: readonly SpriteKey[] = [
  'houndWalk8_t000', 'houndWalk16_t062', 'houndWalk8_t125', 'houndWalk16_t188',
  'houndWalk8_t250', 'houndWalk16_t312', 'houndWalk16_t375', 'houndWalk16_t438',
  'houndWalk8_t500', 'houndWalk16_t562', 'houndWalk8_t625', 'houndWalk16_t688',
  'houndWalk8_t750', 'houndWalk16_t812', 'houndWalk8_t875', 'houndWalk16_t938',
]

export function setHoundWalk16(v: boolean, smooth?: WalkCycleSmooth): void {
  if (v) setWalkCycleOverride('dream-hound', { keys: HOUND_WALK16_KEYS, stridePx: HOUND_WALK8_STRIDE_PX, smooth })
  else setHoundWalk8(true)
}

// 胴が一定の速さで進む横の置き場所 (2026-11-04・開発版だけ ?houndWalkAnchor=trunk)。
//   作者: 16＋溶かすでも「かくかく動いてるように見える／スローだとめっちゃ滑らか」(db answers/hound-walk-16fade-1104)。
//   今の置き場所は床の足が止まる所に合わせてあるので、胴は5コマかけて少しずつ遅れ、1コマで約 22px 前へ跳ぶ (1周に2回)。
//   ここでは胴 (上半身の重心) の体の芯からの横の距離を16枚とも同じにする。代わりに床の足はコマの替わり目で滑る。
//   値 = 1/60 秒ずつ撮って測った、胴を1周の平均へ寄せる画面の px (左向きで測った・絵の px へは 1 / HOUND_WALK16_ART_TO_SCREEN)。
//   記録 = docs/research/2026-11-04-hound-walk-judder-60hz.md
export const HOUND_WALK16_TRUNK_SHIFT_PX: Readonly<Record<string, number>> = {
  houndWalk8_t000: 1.7, houndWalk16_t062: -3.2, houndWalk8_t125: -6.4, houndWalk16_t188: -10.2,
  houndWalk8_t250: -10.2, houndWalk16_t312: -12.5, houndWalk16_t375: 9.8, houndWalk16_t438: -2.3,
  houndWalk8_t500: 13.8, houndWalk16_t562: 9.0, houndWalk8_t625: 9.1, houndWalk16_t688: -2.7,
  houndWalk8_t750: -7.6, houndWalk16_t812: 1.0, houndWalk8_t875: 6.0, houndWalk16_t938: 4.7,
}
// 詰めた PNG の 1px が画面で何 px か (待機の面積から出した大きさ・tests/hound_walk16_1104.test.ts の S と同じ)。
export const HOUND_WALK16_ART_TO_SCREEN = 186 * 0.89 / Math.sqrt(677797)

const FEET_ANCHOR = new Map<string, number>()

// 足の滑りを減らす比べ物 (2026-11-05・開発版だけ ?houndWalkAnchor=half|tick|smooth・決裁箱 2026-11-05-hound-walk-slide)。
//   配布版の trunk は替わり目で床の足が最大 35px 滑る (作者 slide=bit「少し気になる」)。
//   half   = 胴の置き場所と足の置き場所の中間 (どの時も半分ずつ)。
//   tick   = ふだんは胴に合わせ、見えている絵が替わる1拍 (1/60 秒) だけ足に合わせる。
//   smooth = 替わり目の前後で、胴の置き場所から足の置き場所へ連続に寄せて戻す (1コマの真ん中で足・端で胴)。
//   記録 = docs/research/2026-11-05-hound-walk-slide.md
export type HoundWalk16Anchor = 'feet' | 'trunk' | 'half' | 'tick' | 'smooth'
// 1拍で進む1コマの割合の半分 (歩きは 1/60 秒に 10/3 px・1コマ = 1周 ÷ 16)。
const TICK_HALF_FRAC = (10 / 3) / (HOUND_WALK8_STRIDE_PX / 16) / 2

function placeAt(weight: number): void {
  for (const [key, shift] of Object.entries(HOUND_WALK16_TRUNK_SHIFT_PX)) {
    if (!FEET_ANCHOR.has(key)) FEET_ANCHOR.set(key, HOUND_PASS_ANCHOR_X[key]!)
    HOUND_PASS_ANCHOR_X[key] = FEET_ANCHOR.get(key)! - weight * shift / HOUND_WALK16_ART_TO_SCREEN
  }
}

/** 胴に合わせる重み (1 = 胴・0 = 足)。1コマの中の進んだ割合 frac で決まる。 */
export function houndWalk16AnchorWeight(mode: HoundWalk16Anchor, frac: number): number {
  if (mode === 'trunk') return 1
  if (mode === 'half') return 0.5
  if (mode === 'tick') return Math.abs(frac - 0.5) < TICK_HALF_FRAC ? 0 : 1
  if (mode === 'smooth') return 0.5 + 0.5 * Math.cos(2 * Math.PI * frac)
  return 0
}

export function setHoundWalk16Anchor(mode: HoundWalk16Anchor): void {
  placeAt(houndWalk16AnchorWeight(mode, 0))
  setWalkCycleFracHook('dream-hound', mode === 'tick' || mode === 'smooth' ? frac => placeAt(houndWalk16AnchorWeight(mode, frac)) : null)
}

// 遊びながら足の置き場所を切り替える比べ物 (2026-11-05・開発版だけ ?houndWalkToggle=1・V = 置き場所・Y = 溶かす)。
//   順 = 配布版 (trunk) → ① half → ② tick → ③ smooth → 配布版。
export const HOUND_WALK_TOGGLE_ORDER: readonly HoundWalk16Anchor[] = ['trunk', 'half', 'tick', 'smooth']
export function nextHoundWalkToggleAnchor(cur: HoundWalk16Anchor): HoundWalk16Anchor {
  const i = HOUND_WALK_TOGGLE_ORDER.indexOf(cur)
  return HOUND_WALK_TOGGLE_ORDER[(i + 1) % HOUND_WALK_TOGGLE_ORDER.length]!
}
const ANCHOR_LABEL: Record<HoundWalk16Anchor, string> = { trunk: '配布版のまま', half: '① 中間', tick: '② 替わり目だけ足', smooth: '③ 連続に寄せる', feet: '足に合わせる' }
export function houndWalkToggleLabel(anchor: HoundWalk16Anchor, fade: boolean): string {
  return `足の置き場所：${ANCHOR_LABEL[anchor]}／コマのつなぎ：${fade ? 'オン' : 'オフ'}　[V 置き場所・Y つなぎ]`
}
